/* eslint-disable */

/**
 * Dependencies:
 * - Custom select component
 *
 * Required translation strings:
 * - addToCart
 * - noStock
 * - noVariant
 * - onlyXLeft
 */

if (!customElements.get('variant-picker')) {
  class VariantPicker extends HTMLElement {
    constructor() {
      super();
      this.section = this.closest('.js-product');
      this.productForm = this.section.querySelector('.js-product-form-main');
      this.optionSelectors = this.querySelectorAll('.option-selector');
      this.data = this.getProductData();
      this.variant = this.getSelectedVariant();

      this.updateAvailability();
      this.updateAddToCartButton();
      this.initTrouserSelector();
      this.addEventListener('change', this.handleVariantChange.bind(this));
    }

    /**
     * Initialises the two-step waist/inseam selector for trouser products.
     * This runs from the constructor so it works both on the PDP and when the
     * variant-picker is injected into the quick-add drawer via innerHTML.
     */
    initTrouserSelector() {
      const selectorRoot = this.querySelector('[data-trouser-selector]');
      if (!selectorRoot) return;

      const waistContainer = selectorRoot.querySelector('[data-waist-list]');
      const inseamContainer = selectorRoot.querySelector('[data-inseam-list]');
      const waistLabelEl = selectorRoot.querySelector('[data-waist-label]');
      const inseamLabelEl = selectorRoot.querySelector('[data-inseam-label]');
      const sizeInputs = selectorRoot.querySelectorAll('.trouser-size-hidden .js-option');

      if (!waistContainer || !inseamContainer || !sizeInputs.length) return;

      const waistsSet = new Set();
      const inseamsSet = new Set();
      const sizeValues = [];

      sizeInputs.forEach((input) => {
        const { value } = input;
        sizeValues.push({ size: value, input });
        if (!value) return;
        const parts = value.split('/');
        if (parts.length !== 2) return;
        const waist = parts[0].trim();
        const inseam = parts[1].trim();
        if (waist && inseam) {
          waistsSet.add(waist);
          inseamsSet.add(inseam);
        }
      });

      const waists = Array.from(waistsSet).sort((a, b) => Number(a) - Number(b));
      const inseams = Array.from(inseamsSet).sort((a, b) => Number(a) - Number(b));

      let selectedWaist = null;
      let selectedInseam = null;
      const waistButtons = [];
      const inseamButtons = [];

      const createOptionBtn = (label, type) => {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.textContent = label;
        btn.dataset[type] = label;
        btn.className = `opt-label opt-label--btn btn relative text-center trouser-${type}-btn`;
        return btn;
      };

      const setSelectedState = (type, label) => {
        const container = type === 'waist' ? waistContainer : inseamContainer;
        container.querySelectorAll(`.trouser-${type}-btn`).forEach((btn) => {
          const isMatch = btn.dataset[type] === label;
          btn.classList.toggle('is-selected', isMatch);
          btn.classList.toggle('btn--active', isMatch);
        });

        if (type === 'waist' && waistLabelEl) {
          waistLabelEl.textContent = label ? `Size: ${label}` : 'Choose a size';
        }
        if (type === 'inseam' && inseamLabelEl) {
          inseamLabelEl.textContent = label ? `Length: ${label}` : 'Choose a length';
        }
      };

      const updateTrouserAvailability = () => {
        waistButtons.forEach((btn) => {
          const w = btn.dataset.waist;
          let anyExists = false;
          let anyAvailable = false;

          sizeValues.forEach((entry) => {
            const parts = entry.size.split('/');
            if (parts.length !== 2) return;
            const waist = parts[0].trim();
            const inseam = parts[1].trim();
            if (waist !== w) return;
            anyExists = true;
            if (selectedInseam && inseam !== selectedInseam) return;
            if (!entry.input.classList.contains('is-unavailable')) anyAvailable = true;
          });

          btn.classList.toggle('is-nonexistent', !anyExists);
          btn.classList.toggle('is-unavailable', anyExists && !anyAvailable);
          btn.title =
            anyExists && !anyAvailable && window.theme && theme.strings && theme.strings.noStock
              ? theme.strings.noStock
              : '';
        });

        inseamButtons.forEach((btn) => {
          const i = btn.dataset.inseam;
          let anyExists = false;
          let anyAvailable = false;

          sizeValues.forEach((entry) => {
            const parts = entry.size.split('/');
            if (parts.length !== 2) return;
            const waist = parts[0].trim();
            const inseam = parts[1].trim();
            if (inseam !== i) return;
            if (selectedWaist && waist !== selectedWaist) return;
            anyExists = true;
            if (!entry.input.classList.contains('is-unavailable')) anyAvailable = true;
          });

          btn.classList.toggle('is-nonexistent', !anyExists);
          btn.classList.toggle('is-unavailable', anyExists && !anyAvailable);
          btn.title =
            anyExists && !anyAvailable && window.theme && theme.strings && theme.strings.noStock
              ? theme.strings.noStock
              : '';
        });
      };

      const updateVariantSelection = () => {
        if (!selectedWaist || !selectedInseam) return;

        const targetSize = `${selectedWaist}/${selectedInseam}`;
        const match = sizeValues.find((sv) => sv.size === targetSize);

        if (!match) {
          sizeInputs.forEach((input) => { input.checked = false; });
          if (sizeInputs.length > 0) {
            sizeInputs[0].dispatchEvent(new Event('change', { bubbles: true }));
          }
          return;
        }

        if (!match.input.checked) {
          match.input.checked = true;
          match.input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      };

      waists.forEach((w) => {
        const btn = createOptionBtn(w, 'waist');
        btn.addEventListener('click', () => {
          const waistChanged = selectedWaist !== w;
          selectedWaist = w;

          if (waistChanged && selectedInseam) {
            selectedInseam = null;
            setSelectedState('inseam', null);
            sizeInputs.forEach((input) => { input.checked = false; });
          }

          setSelectedState('waist', w);
          updateTrouserAvailability();
          updateVariantSelection();
          this.updateAddToCartButton();
        });
        waistButtons.push(btn);
        waistContainer.appendChild(btn);
      });

      inseams.forEach((i) => {
        const btn = createOptionBtn(i, 'inseam');
        btn.addEventListener('click', () => {
          selectedInseam = i;
          setSelectedState('inseam', i);
          updateTrouserAvailability();
          updateVariantSelection();
        });
        inseamButtons.push(btn);
        inseamContainer.appendChild(btn);
      });

      const syncSelectedFromInputs = () => {
        let current = null;
        sizeInputs.forEach((input) => { if (input.checked) current = input; });
        if (current) {
          const parts = current.value.split('/');
          if (parts.length === 2) {
            selectedWaist = parts[0].trim();
            selectedInseam = parts[1].trim();
            setSelectedState('waist', selectedWaist);
            setSelectedState('inseam', selectedInseam);
          }
        } else {
          selectedWaist = null;
          selectedInseam = null;
          setSelectedState('waist', null);
          setSelectedState('inseam', null);
        }
      };

      updateTrouserAvailability();

      this.addEventListener('change', () => {
        syncSelectedFromInputs();
        updateTrouserAvailability();
      });
    }

    /**
     * Handles 'change' events on the variant picker element.
     * @param {object} evt - Event object.
     */
    handleVariantChange(evt) {
      // If a color option changed, clear all size selections
      if (evt && evt.target) {
        const changedSelector = evt.target.closest('.option-selector');
        if (changedSelector) {
          const optionIndex = changedSelector.dataset.optionIndex;
          const isSizeOption = this.data.product.options[optionIndex] === 'Size';
          
          if (!isSizeOption) {
            // Clear all Size option selections
            this.querySelectorAll('.js-option').forEach((input) => {
              const selector = input.closest('.option-selector');
              if (selector) {
                const idx = selector.dataset.optionIndex;
                if (this.data.product.options[idx] === 'Size') {
                  input.checked = false;
                }
              }
            });
          }
        }
      }

      this.variant = null;

      // Get selected variant data (if variant exists).
      this.variant = this.getSelectedVariant();

      if (this.variant) {
        this.updateMedia();
        this.updateUrl(evt);
        this.updateVariantInput();
      }

      this.updateAddToCartButton();
      this.updateAvailability();
      this.updatePrice();
      this.updateWeight();
      this.updateBarcode();
      this.updateBackorderText();
      this.updatePickupAvailability();
      this.updateSku();
      VariantPicker.updateLabelText(evt);

      this.dispatchEvent(new CustomEvent('on:variant:change', {
        bubbles: true,
        detail: {
          form: this.productForm,
          variant: this.variant,
          product: this.data.product
        }
      }));
    }

    /**
     * Updates the "Add to Cart" button label and disabled state.
     */
    updateAddToCartButton() {
      this.productForm = this.section.querySelector('.js-product-form-main');
      if (!this.productForm) return;

      this.addBtn = this.addBtn || this.productForm.querySelector('[name="add"]');
      const variantAvailable = this.variant && this.variant.available;
      let buttonText = this.variant ? theme.strings.noStock : theme.strings.noVariant;

      const hasOptionSelectors = this.optionSelectors && this.optionSelectors.length > 0;
      const trouserSelector = this.querySelector('[data-trouser-selector]');
      if (trouserSelector) {
        const anySizeChecked = !!trouserSelector.querySelector('.trouser-size-hidden .js-option:checked');
        const waistSelected = !!trouserSelector.querySelector('.trouser-waist-btn.is-selected');
        const inseamSelected = !!trouserSelector.querySelector('.trouser-inseam-btn.is-selected');
        
        // If partial selection (waist or inseam selected but no complete size), show "Select a size"
        if (!anySizeChecked && (waistSelected || inseamSelected)) {
          buttonText = theme.strings.selectSize;
          this.addBtn.disabled = true;
          this.addBtn.textContent = buttonText;
          return;
        }
      }

      if (!this.variant && hasOptionSelectors) {
        // Default: if no variant selected, show "Select a size"
        buttonText = theme.strings.selectSize;
      }

      this.addBtn.disabled = !variantAvailable;
      this.addBtn.textContent = variantAvailable
        ? this.addBtn.dataset.addToCartText
        : buttonText;
    }

    /**
     * Updates the availability status of an option.
     * @param {Element} optionEl - Option element.
     * @param {boolean} exists - Does this option lead to a variant that exists?
     * @param {boolean} available - Does this option lead to a variant that is available to buy?
     */
    static updateOptionAvailability(optionEl, exists, available) {
      const el = optionEl;
      const unavailableText = exists ? theme.strings.noStock : theme.strings.noVariant;
      el.classList.toggle('is-unavailable', !available);
      el.classList.toggle('is-nonexistent', !exists);

      if (optionEl.classList.contains('custom-select__option')) {
        const em = el.querySelector('em');

        if (em) {
          em.hidden = available;
        }

        if (!available) {
          if (em) {
            em.textContent = unavailableText;
          } else {
            el.innerHTML = `${el.innerHTML} <em class="pointer-events-none">${unavailableText}</em>`;
          }
        }
      } else if (!available) {
        el.nextElementSibling.title = unavailableText;
      } else {
        el.nextElementSibling.removeAttribute('title');
      }
    }

    /**
     * Updates the availability status in option selectors.
     */
    updateAvailability() {
      if (this.dataset.showAvailability === 'false') return;
      const { availabilityMethod } = this.dataset; // 'downward' or 'selection'
      let currVariant = this.variant;
      const selectedOptions = this.getSelectedOptions();

      if (!this.variant) {
        currVariant = { options: selectedOptions };
      }

      if (availabilityMethod === 'selection') {
        // Flag all options as unavailable
        this.querySelectorAll('.js-option').forEach((optionEl) => {
          VariantPicker.updateOptionAvailability(optionEl, false, false);
        });

        // Flag selector options as available or sold out, depending on the variant availability
        this.optionSelectors.forEach((selector, selectorIndex) => {
          const optionStatus = new Map();

          this.data.product.variants.forEach((variant) => {
            let matchCount = 0;
            let requiredMatches = 0;

            variant.options.forEach((option, optionIndex) => {
              if (optionIndex === selectorIndex) return;

              const selectedVal = currVariant.options[optionIndex];

              if (selectedVal == null) {
                return;
              }

              requiredMatches += 1;
              if (option === selectedVal) {
                matchCount += 1;
              }
            });

            if (matchCount === requiredMatches) {
              const valueForSelector = variant.options[selectorIndex];
              const prev = optionStatus.get(valueForSelector) || { exists: false, available: false };
              prev.exists = true;
              if (variant.available) {
                prev.available = true;
              }
              optionStatus.set(valueForSelector, prev);
            }
          });

          const options = selector.querySelectorAll('.js-option');
          options.forEach((opt) => {
            const optionValue = selector.dataset.selectorType === 'dropdown' ? opt.dataset.value : opt.value;
            const status = optionStatus.get(optionValue);
            if (status) {
              VariantPicker.updateOptionAvailability(opt, status.exists, status.available);
            }
          });
        });
      } else {
        this.optionSelectors.forEach((selector, selectorIndex) => {
          const options = selector.querySelectorAll('.js-option:not([data-value=""])');
          options.forEach((option) => {
            const optionValue = selector.dataset.selectorType === 'dropdown' ? option.dataset.value : option.value;
            // any available variants with previous options and this one locked in?
            let variantsExist = false;
            let variantsAvailable = false;
            this.data.product.variants.forEach((v) => {
              let matches = 0;
              for (let i = 0; i < selectorIndex; i += 1) {
                if (v.options[i] === selectedOptions[i] || selectedOptions[i] === null) {
                  matches += 1;
                }
              }
              if (v.options[selectorIndex] === optionValue && matches === selectorIndex) {
                variantsExist = true;
                if (v.available) {
                  variantsAvailable = true;
                }
              }
            });
            VariantPicker.updateOptionAvailability(option, variantsExist, variantsAvailable);
          });
        });
      }
    }

    /**
     * Updates the backorder text and visibility.
     */
    updateBackorderText() {
      this.backorder = this.backorder || this.section.querySelector('.backorder');
      if (!this.backorder) return;

      let hideBackorder = true;

      if (this.variant && this.variant.available) {
        const { inventory } = this.data.formatted[this.variant.id];

        if (this.variant.inventory_management && inventory === 'none') {
          const backorderProdEl = this.backorder.querySelector('.backorder__product');
          const prodTitleEl = this.section.querySelector('.product-title');
          const variantTitle = this.variant.title.includes('Default')
            ? ''
            : ` - ${this.variant.title}`;

          backorderProdEl.textContent = `${prodTitleEl.textContent}${variantTitle}`;
          hideBackorder = false;
        }
      }

      this.backorder.hidden = hideBackorder;
    }

    /**
     * Updates the color option label text.
     * @param {object} evt - Event object
     */
    static updateLabelText(evt) {
      const selector = evt.target.closest('.option-selector');
      if (selector.dataset.selectorType === 'dropdown') return;

      const colorText = selector.querySelector('.js-color-text');
      if (!colorText) return;

      colorText.textContent = evt.target.nextElementSibling.querySelector('.js-value').textContent;
    }

    /**
     * Updates the product media.
     */
    updateMedia() {
      if (!this.variant.featured_media) return;

      if (this.section.matches('quick-add-drawer')) {
        this.section.updateMedia(this.variant.featured_media.id);
      } else {
        this.mediaGallery = this.mediaGallery || this.section.querySelector('media-gallery');
        if (!this.mediaGallery) return;

        const variantMedia = this.mediaGallery.querySelector(
          `[data-media-id="${this.variant.featured_media.id}"]`
        );
        this.mediaGallery.setActiveMedia(variantMedia, true, true);
      }
    }

    /**
     * Updates the pick up availability.
     */
    updatePickupAvailability() {
      this.pickUpAvailability =
        this.pickUpAvailability || this.section.querySelector('pickup-availability');
      if (!this.pickUpAvailability) return;

      if (this.variant && this.variant.available) {
        this.pickUpAvailability.getAvailability(this.variant.id);
      } else {
        this.pickUpAvailability.removeAttribute('available');
        this.pickUpAvailability.innerHTML = '';
      }
    }

    /**
     * Updates the price.
     */
    updatePrice() {
      this.price = this.price || this.section.querySelector('.product-info__price > .price');
      if (!this.price) return;

      let displayVariant = this.variant;
      
      // If no complete variant selected, find first available variant matching selected options
      if (!displayVariant) {
        const selectedOptions = this.getSelectedOptions();
        const matchingVariant = this.data.product.variants.find((v) => {
          return v.available && selectedOptions.every((opt, idx) => !opt || v.options[idx] === opt);
        });
        if (matchingVariant) {
          displayVariant = matchingVariant;
        }
      }

      if (displayVariant) {
        const priceCurrentEl = this.price.querySelector('.price__current');
        const priceWasEl = this.price.querySelector('.price__was');
        const unitPriceEl = this.price.querySelector('.unit-price');

        // Update current price and original price if on sale.
        priceCurrentEl.innerHTML = this.data.formatted[displayVariant.id].price;
        if (priceWasEl)
          priceWasEl.innerHTML = this.data.formatted[displayVariant.id].compareAtPrice || '';

        // Update unit price, if specified.
        if (displayVariant.unit_price_measurement) {
          const valueEl = this.price.querySelector('.unit-price__price');
          const unitEl = this.price.querySelector('.unit-price__unit');
          const value = displayVariant.unit_price_measurement.reference_value;
          const unit = displayVariant.unit_price_measurement.reference_unit;

          valueEl.innerHTML = this.data.formatted[displayVariant.id].unitPrice;
          unitEl.textContent = value === 1 ? unit : `${value} ${unit}`;
        }

        unitPriceEl.hidden = !displayVariant.unit_price_measurement;
        this.price.classList.toggle(
          'price--on-sale',
          displayVariant.compare_at_price > displayVariant.price
        );
        this.price.classList.toggle('price--sold-out', !displayVariant.available);
      }

      this.price.querySelector('.price__default').hidden = !displayVariant;
      this.price.querySelector('.price__no-variant').hidden = displayVariant;
    }

    /**
     * Updates the weight.
     */
    updateWeight() {
      this.weights = this.weights || this.section.querySelectorAll('.product-info__weight');
      if (this.weights.length === 0) return;

      const weightAvailable = this.variant && this.variant.weight > 0;
      this.weights.forEach((weight) => {
        weight.textContent = weightAvailable ? this.data.formatted[this.variant.id].weight : '';
        weight.hidden = !weightAvailable;
      });
    }

    /**
     * Updates the Barcode.
     */
    updateBarcode() {
      this.barcodes = this.barcodes || this.section.querySelectorAll('.product-info__barcode-value');
      if (this.barcodes.length === 0) return;

      const barcodeAvailable = this.variant && this.variant.barcode;
      this.barcodes.forEach((barcode) => {
        barcode.textContent = barcodeAvailable ? this.variant.barcode : '';
        barcode.parentNode.hidden = !barcodeAvailable;
      });
    }

    /**
     * Updates the SKU.
     */
    updateSku() {
      this.sku = this.sku || this.section.querySelector('.product-sku__value');
      if (!this.sku) return;

      const skuAvailable = this.variant && this.variant.sku;
      this.sku.textContent = skuAvailable ? this.variant.sku : '';
      this.sku.parentNode.hidden = !skuAvailable;
    }

    /**
     * Updates the url with the selected variant id.
     * @param {object} evt - Event object.
     */
    updateUrl(evt) {
      if (!evt || evt.type !== 'change' || this.dataset.updateUrl === 'false') return;
      window.history.replaceState({}, '', `${this.dataset.url}?variant=${this.variant.id}`);
    }

    /**
     * Updates the value of the hidden [name="id"] form inputs.
     */
    updateVariantInput() {
      this.forms =
        this.forms || this.section.querySelectorAll('.js-product-form-main, .js-instalments-form');

      this.forms.forEach((form) => {
        const input = form.querySelector('input[name="id"]');
        input.value = this.variant.id;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }

    /**
     * Gets the selected option element from each selector.
     * @returns {Array}
     */
    getSelectedOptions() {
      const selectedOptions = [];

      this.optionSelectors.forEach((selector) => {
        if (selector.dataset.selectorType === 'dropdown') {
          const btn = selector.querySelector('.custom-select__btn');
          selectedOptions.push(btn ? btn.textContent.trim() : null);
        } else {
          const checked = selector.querySelector('input:checked');
          selectedOptions.push(checked ? checked.value : null);
        }
      });

      return selectedOptions;
    }

    /**
     * Gets the product data.
     * @returns {?object}
     */
    getProductData() {
      const dataEl = this.querySelector('[type="application/json"]');
      return JSON.parse(dataEl.textContent);
    }

    /**
     * Get selected variant data.
     * @returns {?object} Variant object, or null if one is not selected.
     */
    getSelectedVariant() {
      const selectedOptions = this.getSelectedOptions();
      return this.data.product.variants.find(
        (v) => v.options.every((val, index) => val === selectedOptions[index])
      );
    }
  }

  customElements.define('variant-picker', VariantPicker);
}
